import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { AssetUIModel, ClientEnvironment, ENVIRONMENT } from '@thehubz/client/core';
import { AssetsBulkEditApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, MULTI_VALUE } from '@thehubz/shared/model';
import { firstValueFrom } from 'rxjs';
import { AssetCommonService } from '../../../services/asset-common.service';
import { AssetDetailsBuyoutRightsComponent } from '../../asset-detail-view/asset-usage-rights/asset-details-buyout-rights/asset-details-buyout-rights.component';
import { BuyoutUtilsService } from '../buyout-utils.service';
import { updateRights } from '../buyout.utils';

@Component({
  selector: 'thehubz-asset-buyout-rights-summary',
  templateUrl: './asset-buyout-rights-summary.component.html',
  styleUrls: ['./asset-buyout-rights-summary.component.scss'],
  standalone: true,
  imports: [MatIconModule, MatDialogModule, AssetDetailsBuyoutRightsComponent],
})
export class AssetBuyoutRightsSummaryComponent implements OnInit {
  assetListFiles: AssetUIModel[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { updatedAssetLists: AssetUIModel[]; allTilesUsedFlag?: boolean },
    @Inject(ENVIRONMENT) private env: ClientEnvironment,
    private assetCommonService: AssetCommonService,
    private buyoutUtilsService: BuyoutUtilsService,
    private dialogRef: MatDialogRef<AssetBuyoutRightsSummaryComponent>,
    private assetsBulkEditService: AssetsBulkEditApiService,
  ) {}

  ngOnInit(): void {
    this.assetListFiles = this.data.updatedAssetLists;

    if (this.env.brandingKey === CustomClients.Standard && this.assetListFiles.length > 1) {
      // load buyout bulk edit model
      const assetIds = this.assetListFiles.map((asset) => asset['_id']);
      this.assetsBulkEditService.assetsBulkEditControllerGetMakeBuyoutEditModel({ ids: assetIds }).subscribe((response) => {
        if (response) {
          const firstSelectedAsset = this.assetListFiles[0];
          firstSelectedAsset.makeBuyoutRights = response.mergedData;
          response.mergedData.forEach((buyoutRight, index) => {
            if (firstSelectedAsset.makeBuyoutRights?.[index] && firstSelectedAsset.makeBuyoutRights[index].buyoutRightsTitle === buyoutRight.buyoutRightsTitle)
              // we have to show 'Multiple-values' later, in disable state
              // remove filer once backend supports multiple values
              firstSelectedAsset.makeBuyoutRights[index].buyoutRights = response.mergedData[index].buyoutRights
                .filter((right: AnyType) => right !== MULTI_VALUE)
                .map((right) => ({
                  ...right,
                  territories: (right.territories || []).filter((territory: AnyType) => territory !== MULTI_VALUE),
                }));
          });
          firstSelectedAsset.notes = response.mergedNotes;
          this.assetCommonService.assetDataSubject$.next(firstSelectedAsset);
        }
      });
    }
  }

  /**
   * Add more buyout rights
   */
  async moreBuyoutRights() {
    const updatedAsset = await firstValueFrom(this.assetCommonService.assetDataSubject$);
    if (updatedAsset) {
      this.assetListFiles.forEach((asset) => {
        asset.usageRights = updatedAsset.usageRights;
      });
    }
    const singleAssetUpdate = this.assetListFiles?.length === 1 ? true : false;
    this.buyoutUtilsService.assetBuyoutRightsDialogs(this.assetListFiles, false, singleAssetUpdate).subscribe((update) => {
      const updatedAsset = update.updatedAsset;
      const currentAsset = this.assetListFiles[0];

      updateRights(updatedAsset.usageRights, currentAsset.usageRights, (newRights) => (currentAsset.usageRights = newRights));

      updateRights(updatedAsset.makeBuyoutRights, currentAsset.makeBuyoutRights, (newRights) => (currentAsset.makeBuyoutRights = newRights));

      currentAsset.status = updatedAsset.status;
      this.assetCommonService.assetDataSubject$.next(currentAsset);
    });
  }

  closeDialog() {
    this.dialogRef.close(true);
    this.assetCommonService.unMatchedUsageRight$.next([]);
  }
}
